import type { Distance, Point, SpatialRectangle } from "@pdfking/math"
import { Abs, Max, Min, ToRadians } from "@pdfking/math"
import { ZERO_RADIAN } from "@pdfking/math/constants"
import { NullQ } from "@pdfking/structs"

import type { GrowFold, GrowJoint, GrowLeaf, ResolvedGrow } from "#internal"

import { BoxCorners, GrowLeafBoxed, GrowLeafPadded } from "./GrowLeafShape"

type Box = SpatialRectangle<Distance>

// A cell of the sheet: a piece, or the share of one a crease cut off it, and
// how many creases it has
type Cell = { box: Box; corners: Point<Distance>[]; leaf: number; creases: number }

type Edge = { a: number; b: number; angle: GrowJoint["angle"]; step: number; ajar: number }

// `corners` held within `box`: a piece's share of a tapered one keeps the
// taper that falls in it
const clamped = (corners: Point<Distance>[], box: Box): Point<Distance>[] =>
  corners.map(({ x, y }) => ({
    x: Max(box.x, Min(box.x + box.width, x)) as Distance,
    y: Max(box.y, Min(box.y + box.height, y)) as Distance,
  }))

/**
 * The grow as a sheet folding from the piece `anchor` picks, its cells
 * parents first, each with the piece it is of (see GrowFold); none when the grow has no crease. A crease
 * joins the cells either side that touch it along its span, each to the one
 * across from it: between two rows, a cell per column, all turning together.
 * Where the two sides don't line up each is cut where the other's cells meet
 * — a strip along a row, a cell per cell of the row —; a side creased already
 * is cut from the start, and its creases with it. Of two ways to a cell, the
 * cell turns on the crease that opens later: the other one, opening earlier,
 * moves with it, the sheet folded on it.
 */
export const GrowFolds = (grow: ResolvedGrow, anchor: (leaf: GrowLeaf) => boolean): GrowFold[] => {
  // A sheet of one piece is its one cell; of more and no crease, none
  if (!grow.joints.length && grow.leaves.length > 1) return []
  const eps = Max(grow.size.width, grow.size.height) * 1e-9
  const near = (a: number, b: number) => Abs(a - b) <= eps

  // Where each piece is cut from the start, on x and on y
  const forced = new Map<number, { x: number[]; y: number[] }>()
  for (;;) {
    const creased = crease_cells(grow, forced, eps, near)
    if ("cells" in creased) return tree_of(grow, creased.cells, creased.edges, anchor)
    const cuts = forced.get(creased.leaf) ?? { x: [], y: [] }
    forced.set(creased.leaf, { ...cuts, [creased.axis]: [...cuts[creased.axis], ...creased.at] })
  }
}

// A box cut at `xs` and `ys` that fall inside it.
const grid = (box: Box, xs: number[], ys: number[], eps: number): Box[] => {
  const bounds = (from: number, size: number, at: number[]) => [
    from,
    ...[...new Set(at)].filter(c => c > from + eps && c < from + size - eps).sort((a, b) => a - b),
    from + size,
  ]
  const bx = bounds(box.x, box.width, xs)
  const by = bounds(box.y, box.height, ys)
  return bx.slice(1).flatMap((x1, i) =>
    by.slice(1).map((y1, j) => ({
      x: bx[i]! as Distance,
      y: by[j]! as Distance,
      width: (x1 - bx[i]!) as Distance,
      height: (y1 - by[j]!) as Distance,
    }))
  )
}

// The cells, each piece cut as `forced` says, and the creases between them;
// or the piece a crease would cut that is creased already, and where.
const crease_cells = (
  grow: ResolvedGrow,
  forced: ReadonlyMap<number, { x: number[]; y: number[] }>,
  eps: number,
  near: (a: number, b: number) => boolean
): { cells: Cell[]; edges: Edge[] } | { leaf: number; axis: "x" | "y"; at: number[] } => {
  const cells: Cell[] = grow.leaves.flatMap((leaf, i) =>
    grid(GrowLeafPadded(leaf), forced.get(i)?.x ?? [], forced.get(i)?.y ?? [], eps).map(box => ({
      box,
      corners: GrowLeafBoxed(leaf) ? BoxCorners(box) : clamped(leaf.corners, box),
      leaf: i,
      creases: 0,
    }))
  )
  const edges: Edge[] = []

  for (const joint of grow.joints) {
    const is_x = joint.axis === "x"
    // Along x the layout before is left of the crease, along y above it
    const end = (box: Box) => (is_x ? box.x + box.width : box.y)
    const begin = (box: Box) => (is_x ? box.x : box.y + box.height)
    const low = (box: Box) => (is_x ? box.y : box.x)
    const high = (box: Box) => (is_x ? box.y + box.height : box.x + box.width)
    const [span_low, span_high] = joint.span
    const touching = (leaves: number[], edge: (box: Box) => number) =>
      cells.flatMap((cell, i) =>
        leaves.includes(cell.leaf) &&
        near(edge(cell.box), joint.at) &&
        low(cell.box) < span_high - eps &&
        high(cell.box) > span_low + eps
          ? [i]
          : []
      )
    const before = touching(joint.before, end)
    const after = touching(joint.after, begin)
    // Where a side's cells meet: its ends are no cut
    const meets = (side: number[]) => {
      const ends = side.flatMap(i => [low(cells[i]!.box), high(cells[i]!.box)])
      return ends.filter(c => c > Min(...ends) + eps && c < Max(...ends) - eps)
    }
    // What cuts each side: where the other's cells meet
    const cuts = [meets(after), meets(before)]

    // A cell cut at `at` where that falls inside it
    const inside_of = (i: number, at: number[]) =>
      [...new Set(at)]
        .filter(c => c > low(cells[i]!.box) + eps && c < high(cells[i]!.box) - eps)
        .sort((a, b) => a - b)
    const creased = [before, after].flatMap((side, s) =>
      side.flatMap(i => (cells[i]!.creases && inside_of(i, cuts[s]!).length ? [[i, s]] : []))
    )[0]
    if (!NullQ(creased))
      return {
        leaf: cells[creased[0]!]!.leaf,
        axis: is_x ? "y" : "x",
        at: inside_of(creased[0]!, cuts[creased[1]!]!),
      }
    const cut = (i: number, at: number[]): number[] => {
      const cell = cells[i]!
      const inside = inside_of(i, at)
      if (!inside.length) return [i]
      const bounds = [low(cell.box), ...inside, high(cell.box)]
      const part = (from: number, to: number): Cell => {
        const box: Box = is_x
          ? { ...cell.box, y: from as Distance, height: (to - from) as Distance }
          : { ...cell.box, x: from as Distance, width: (to - from) as Distance }
        return { ...cell, box, corners: clamped(cell.corners, box) }
      }
      cells[i] = part(bounds[0]!, bounds[1]!)
      return [
        i,
        ...bounds.slice(1, -1).map((from, k) => cells.push(part(from, bounds[k + 2]!)) - 1),
      ]
    }
    const sides = [before.flatMap(i => cut(i, cuts[0]!)), after.flatMap(i => cut(i, cuts[1]!))]
    // Each cell creased to the one across from it, the one it overlaps
    for (const a of sides[0]!)
      for (const b of sides[1]!) {
        const [p, q] = [cells[a]!.box, cells[b]!.box]
        if (Min(high(p), high(q)) - Max(low(p), low(q)) <= eps) continue
        edges.push({ a, b, angle: joint.angle, step: joint.step, ajar: joint.ajar })
        cells[a]!.creases++
        cells[b]!.creases++
      }
  }
  return { cells, edges }
}

// The cells as a tree from the anchor's (see GrowFolds).
const tree_of = (
  grow: ResolvedGrow,
  cells: Cell[],
  edges: Edge[],
  anchor: (leaf: GrowLeaf) => boolean
): GrowFold[] => {
  const root = cells.findIndex(cell => anchor(grow.leaves[cell.leaf]!))
  if (root < 0) throw new Error("grow folds from no piece")

  // From the anchor out, the crease opening latest first, the cell nearest
  // the anchor first among equals
  const order = [root]
  const place = new Map([[root, 0]])
  const tree: GrowFold[] = [
    {
      ajar: 0,
      angle: ZERO_RADIAN,
      box: cells[root]!.box,
      corners: cells[root]!.corners,
      parent: undefined,
      step: 0,
      leaf: cells[root]!.leaf,
    },
  ]
  for (;;) {
    let best: { edge: Edge; from: number; to: number } | undefined
    for (const edge of edges) {
      const from = place.has(edge.a) ? edge.a : edge.b
      const to = from === edge.a ? edge.b : edge.a
      if (!place.has(from) || place.has(to)) continue
      if (
        !best ||
        edge.step > best.edge.step ||
        (edge.step === best.edge.step && place.get(from)! < place.get(best.from)!)
      )
        best = { edge, from, to }
    }
    if (!best) break
    place.set(best.to, order.length)
    order.push(best.to)
    tree.push({
      ajar: best.edge.ajar,
      angle: ToRadians(best.edge.angle),
      box: cells[best.to]!.box,
      corners: cells[best.to]!.corners,
      parent: place.get(best.from)!,
      step: best.edge.step,
      leaf: cells[best.to]!.leaf,
    })
  }
  if (order.length !== cells.length) throw new Error("grow folds: a piece is on no crease")
  return tree
}
